import React, { useState, Component } from 'react';
import { PlayIcon, PlusIcon, Trash2Icon } from 'lucide-react';
interface TestData {
  prototypeLink: string;
  featureId: string;
  personaIds: string[];
  testType: string;
  componentFocuses: string[];
  hypothesis: string;
  isRun: boolean;
}
interface PersonaCredential {
  email: string;
  password: string;
  rolePermissions: string;
}
interface DefectEntry {
  id: string;
  description: string;
  severity: string;
  status: string;
  uatCaseId: string;
  retest: string;
}
interface UATTestCase {
  id: string;
  functionalArea: string;
  title: string;
  priority: string;
  personaIds: string[];
  steps: string[];
  expectedResult: string;
  actualResult: string;
  status: string;
  defectId: string;
  notes: string;
  personaCredentials: Record<string, PersonaCredential>;
}
function createEmptyTestCase(index: number): UATTestCase {
  return {
    id: `UAT-${String(index).padStart(3, '0')}`,
    functionalArea: '',
    title: '',
    priority: '',
    personaIds: [],
    steps: ['', '', ''],
    expectedResult: '',
    actualResult: '',
    status: '',
    defectId: '',
    notes: '',
    personaCredentials: {}
  };
}
interface TestTemplateProps {
  sectionId: string;
  featureOptions: {
    id: string;
    name: string;
  }[];
  personaOptions: {
    id: string;
    name: string;
  }[];
  associations: Record<string, string[]>;
  testData: TestData;
  onTestDataChange: (data: TestData) => void;
  onRunTest: () => void;
}
export function TestTemplate({
  sectionId,
  featureOptions,
  personaOptions,
  associations,
  testData,
  onTestDataChange,
  onRunTest
}: TestTemplateProps) {
  const {
    prototypeLink,
    featureId,
    personaIds,
    testType,
    componentFocuses,
    hypothesis,
    isRun
  } = testData;
  const [uatTestCases, setUatTestCases] = useState<UATTestCase[]>([createEmptyTestCase(1)]);
  const [defects, setDefects] = useState<DefectEntry[]>([]);
  // Get associated persona IDs for the selected feature
  const associatedPersonaIds = featureId ? associations[featureId] || [] : [];
  const availablePersonas = personaOptions.filter(p => associatedPersonaIds.includes(p.id));
  const handleSelectFeature = (id: string) => {
    if (isRun) return;
    const newFeatureId = featureId === id ? '' : id;
    const newAssociatedIds = newFeatureId ? associations[newFeatureId] || [] : [];
    const newPersonaIds = newFeatureId ? newAssociatedIds.filter(pid => personaOptions.some(p => p.id === pid)) : [];
    onTestDataChange({
      ...testData,
      featureId: newFeatureId,
      personaIds: newPersonaIds
    });
  };
  const handleTogglePersona = (id: string) => {
    if (isRun) return;
    const next = personaIds.includes(id) ? personaIds.filter(pid => pid !== id) : [...personaIds, id];
    onTestDataChange({
      ...testData,
      personaIds: next
    });
  };
  const handleSelectTestType = (type: string) => {
    if (isRun) return;
    onTestDataChange({
      ...testData,
      testType: testType === type ? '' : type,
      componentFocuses: testType === type ? [] : testData.componentFocuses
    });
  };
  const handleToggleComponentFocus = (focus: string) => {
    if (isRun) return;
    const next = componentFocuses.includes(focus) ? componentFocuses.filter(f => f !== focus) : [...componentFocuses, focus];
    onTestDataChange({
      ...testData,
      componentFocuses: next
    });
  };
  const handleHypothesisChange = (value: string) => {
    if (isRun) return;
    onTestDataChange({
      ...testData,
      hypothesis: value
    });
  };
  const handlePrototypeLinkChange = (value: string) => {
    if (isRun) return;
    onTestDataChange({
      ...testData,
      prototypeLink: value
    });
  };
  const POST_RUN_FIELDS: (keyof UATTestCase)[] = ['actualResult', 'status', 'defectId', 'notes'];
  const handleUpdateTestCase = (index: number, field: keyof UATTestCase, value: any) => {
    // Pre-run fields are locked after run; post-run fields are only editable after run
    if (isRun && !POST_RUN_FIELDS.includes(field)) return;
    if (!isRun && POST_RUN_FIELDS.includes(field)) return;
    setUatTestCases(prev => prev.map((tc, i) => i === index ? {
      ...tc,
      [field]: value
    } : tc));
  };
  const handleToggleTestCasePersona = (tcIndex: number, personaId: string) => {
    if (isRun) return;
    setUatTestCases(prev => prev.map((tc, i) => {
      if (i !== tcIndex) return tc;
      const next = tc.personaIds.includes(personaId) ? tc.personaIds.filter(id => id !== personaId) : [...tc.personaIds, personaId];
      return {
        ...tc,
        personaIds: next
      };
    }));
  };
  const handleUpdateStep = (tcIndex: number, stepIndex: number, value: string) => {
    if (isRun) return;
    setUatTestCases(prev => prev.map((tc, i) => {
      if (i !== tcIndex) return tc;
      const newSteps = [...tc.steps];
      newSteps[stepIndex] = value;
      return {
        ...tc,
        steps: newSteps
      };
    }));
  };
  const handleAddStep = (tcIndex: number) => {
    if (isRun) return;
    setUatTestCases(prev => prev.map((tc, i) => i === tcIndex ? {
      ...tc,
      steps: [...tc.steps, '']
    } : tc));
  };
  const handleRemoveStep = (tcIndex: number, stepIndex: number) => {
    if (isRun) return;
    setUatTestCases(prev => prev.map((tc, i) => {
      if (i !== tcIndex) return tc;
      if (tc.steps.length <= 1) return tc;
      return {
        ...tc,
        steps: tc.steps.filter((_, si) => si !== stepIndex)
      };
    }));
  };
  const handleAddTestCase = () => {
    if (isRun) return;
    setUatTestCases(prev => [...prev, createEmptyTestCase(prev.length + 1)]);
  };
  const handleRemoveTestCase = (index: number) => {
    if (isRun) return;
    if (uatTestCases.length <= 1) return;
    setUatTestCases(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((tc, i) => ({
        ...tc,
        id: `UAT-${String(i + 1).padStart(3, '0')}`
      }));
    });
  };
  const handleUpdatePersonaCredential = (tcIndex: number, personaId: string, field: keyof PersonaCredential, value: string) => {
    if (isRun) return;
    setUatTestCases(prev => prev.map((tc, i) => {
      if (i !== tcIndex) return tc;
      const existing = tc.personaCredentials[personaId] || {
        email: '',
        password: '',
        rolePermissions: ''
      };
      return {
        ...tc,
        personaCredentials: {
          ...tc.personaCredentials,
          [personaId]: {
            ...existing,
            [field]: value
          }
        }
      };
    }));
  };
  const handleAddDefect = () => {
    setDefects(prev => [...prev, {
      id: `DEF-${String(prev.length + 1).padStart(3, '0')}`,
      description: '',
      severity: '',
      status: '',
      uatCaseId: '',
      retest: ''
    }]);
  };
  const handleUpdateDefect = (index: number, field: keyof DefectEntry, value: string) => {
    setDefects(prev => prev.map((d, i) => i === index ? {
      ...d,
      [field]: value
    } : d));
  };
  const handleRemoveDefect = (index: number) => {
    setDefects(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((d, i) => ({
        ...d,
        id: `DEF-${String(i + 1).padStart(3, '0')}`
      }));
    });
  };
  const canRun = !!featureId;
  return <div className="mt-6 space-y-6" data-id="element-409">
      {/* Feature Selection */}
      <div data-id="element-410">
        <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2" data-id="element-411">
          Which feature are you testing?
        </label>
        {featureOptions.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-412">
            {featureOptions.map(feature => {
          const isSelected = featureId === feature.id;
          return <button key={feature.id} type="button" onClick={() => handleSelectFeature(feature.id)} disabled={isRun} className={`px-3 py-1.5 text-sm rounded-none border transition-colors duration-100 ${isSelected ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'} ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-413">
                  {feature.name}
                </button>;
        })}
          </div> : <p className="text-sm text-stone-400 italic" data-id="element-414">
            No features defined yet. Add features in the Feature Hub.
          </p>}
      </div>

      {/* Prototype Link */}
      <div data-id="element-415">
        <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-416">
          Test Prototype Link
        </label>
        <input type="url" value={prototypeLink} onChange={e => handlePrototypeLinkChange(e.target.value)} placeholder="https://..." disabled={isRun} className={`w-full text-sm outline-none rounded-none px-3 py-2.5 border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white transition-colors duration-100 placeholder:text-stone-400 ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-417" />
      </div>

      {/* UAT Test Cases */}
      <div data-id="element-418">
        <div data-id="element-419">
          <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-4" data-id="element-420">
            Define your test cases
          </label>

          <div className="space-y-6" data-id="element-421">
            {uatTestCases.map((tc, tcIndex) => <div key={tcIndex} className="border border-stone-200 rounded-none overflow-hidden" data-id="element-422">
                {/* Blue header banner */}

                <div className="p-4" data-id="element-423">
                  {/* Table */}
                  <table className="w-full border-collapse text-sm" data-id="element-424">
                    <thead data-id="element-425">
                      <tr data-id="element-426">
                        <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold w-[120px] border border-[#0000ff]" data-id="element-427">
                          Test Case
                        </th>
                        <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff]" data-id="element-428">
                          Test Details
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-429">
                      {/* ID */}
                      <tr className={isRun ? 'opacity-50' : ''} data-id="element-430">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-431">
                          ID
                        </td>
                        <td className="px-3 py-2.5 text-stone-600 border border-stone-200" data-id="element-432">
                          {tc.id}
                        </td>
                      </tr>

                      {/* Title */}
                      <tr className={isRun ? 'opacity-50' : ''} data-id="element-433">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-434">
                          Title
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-435">
                          <input type="text" value={tc.title} onChange={e => handleUpdateTestCase(tcIndex, 'title', e.target.value)} placeholder="[Brief description of test case]" disabled={isRun} className={`w-full text-sm outline-none bg-transparent placeholder:text-stone-400 ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-436" />
                        </td>
                      </tr>

                      {/* Priority */}
                      <tr className={isRun ? 'opacity-50' : ''} data-id="element-437">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-438">
                          Priority
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-439">
                          <div className="flex gap-2" data-id="element-440">
                            <button type="button" onClick={() => handleUpdateTestCase(tcIndex, 'priority', tc.priority === 'High' ? '' : 'High')} disabled={isRun} className={`px-3 py-1 text-sm rounded-none border transition-colors duration-100 ${tc.priority === 'High' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'} ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-441">
                              High
                            </button>
                            <button type="button" onClick={() => handleUpdateTestCase(tcIndex, 'priority', tc.priority === 'Medium' ? '' : 'Medium')} disabled={isRun} className={`px-3 py-1 text-sm rounded-none border transition-colors duration-100 ${tc.priority === 'Medium' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'} ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-442">
                              Medium
                            </button>
                            <button type="button" onClick={() => handleUpdateTestCase(tcIndex, 'priority', tc.priority === 'Low' ? '' : 'Low')} disabled={isRun} className={`px-3 py-1 text-sm rounded-none border transition-colors duration-100 ${tc.priority === 'Low' ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'} ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-443">
                              Low
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* UAT Persona */}
                      <tr className={isRun ? 'opacity-50' : ''} data-id="element-444">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-445">
                          UAT Persona
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-446">
                          {personaOptions.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-447">
                              {personaOptions.map(persona => {
                          const isSelected = tc.personaIds.includes(persona.id);
                          return <button key={persona.id} type="button" onClick={() => handleToggleTestCasePersona(tcIndex, persona.id)} disabled={isRun} className={`px-3 py-1 text-sm rounded-none border transition-colors duration-100 ${isSelected ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'} ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-448">
                                    {persona.name}
                                  </button>;
                        })}
                            </div> : <p className="text-sm text-stone-400 italic" data-id="element-449">
                              No personas available. Add personas in the Persona
                              Hub.
                            </p>}
                        </td>
                      </tr>

                      {/* Steps */}
                      <tr className={isRun ? 'opacity-50' : ''} data-id="element-450">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-451">
                          Steps
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-452">
                          <div className="space-y-2" data-id="element-453">
                            {tc.steps.map((step, stepIndex) => <div key={stepIndex} className="flex items-center gap-2" data-id="element-454">
                                <span className="text-xs text-stone-400 flex-shrink-0 w-12" data-id="element-455">
                                  Step {stepIndex + 1}:
                                </span>
                                <input type="text" value={step} onChange={e => handleUpdateStep(tcIndex, stepIndex, e.target.value)} placeholder="[Action to perform]" disabled={isRun} className={`flex-1 text-sm outline-none border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white rounded-none px-2 py-1.5 placeholder:text-stone-400 transition-colors duration-100 ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-456" />
                                {tc.steps.length > 1 && !isRun && <button type="button" onClick={() => handleRemoveStep(tcIndex, stepIndex)} className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-none transition-colors duration-100" aria-label={`Remove step ${stepIndex + 1}`} data-id="element-457">
                                    <Trash2Icon className="w-3 h-3" data-id="element-458" />
                                  </button>}
                              </div>)}
                            {!isRun && <button type="button" onClick={() => handleAddStep(tcIndex)} className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 transition-colors duration-100 mt-1" data-id="element-459">
                                <PlusIcon className="w-3 h-3" data-id="element-460" />
                                <span data-id="element-461">Add Step</span>
                              </button>}
                          </div>
                        </td>
                      </tr>

                      {/* Expected Result */}
                      <tr className={isRun ? 'opacity-50' : ''} data-id="element-462">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-463">
                          Expected Result
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-464">
                          <textarea value={tc.expectedResult} onChange={e => handleUpdateTestCase(tcIndex, 'expectedResult', e.target.value)} placeholder="[What should happen if test is successful]" rows={2} disabled={isRun} className={`w-full text-sm outline-none border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white rounded-none px-2 py-1.5 placeholder:text-stone-400 transition-colors duration-100 resize-none ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-465" />
                        </td>
                      </tr>

                      {/* Actual Result - editable only after test is run */}
                      <tr className={!isRun ? 'opacity-70' : ''} data-id="element-466">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-467">
                          Actual Result
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-468">
                          <textarea value={tc.actualResult} onChange={e => handleUpdateTestCase(tcIndex, 'actualResult', e.target.value)} placeholder={isRun ? '[Record what actually happened]' : 'Available after test is run'} rows={2} disabled={!isRun} className={`w-full text-sm outline-none border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white rounded-none px-2 py-1.5 placeholder:text-stone-400 transition-colors duration-100 resize-none ${!isRun ? 'cursor-not-allowed' : ''}`} data-id="element-469" />
                        </td>
                      </tr>

                      {/* Status - editable only after test is run */}
                      <tr className={!isRun ? 'opacity-70' : ''} data-id="element-470">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-471">
                          Status
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-472">
                          <div className={`flex gap-2 ${!isRun ? 'pointer-events-none' : ''}`} data-id="element-473">
                            <button type="button" onClick={() => handleUpdateTestCase(tcIndex, 'status', tc.status === 'Pass' ? '' : 'Pass')} disabled={!isRun} className={`px-3 py-1 text-sm rounded-none border transition-colors duration-100 ${tc.status === 'Pass' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`} data-id="element-474">
                              Pass
                            </button>
                            <button type="button" onClick={() => handleUpdateTestCase(tcIndex, 'status', tc.status === 'Fail' ? '' : 'Fail')} disabled={!isRun} className={`px-3 py-1 text-sm rounded-none border transition-colors duration-100 ${tc.status === 'Fail' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`} data-id="element-475">
                              Fail
                            </button>
                            <button type="button" onClick={() => handleUpdateTestCase(tcIndex, 'status', tc.status === 'Blocked' ? '' : 'Blocked')} disabled={!isRun} className={`px-3 py-1 text-sm rounded-none border transition-colors duration-100 ${tc.status === 'Blocked' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`} data-id="element-476">
                              Blocked
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Defect ID - editable only after test is run */}
                      <tr className={!isRun ? 'opacity-70' : ''} data-id="element-477">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-478">
                          Defect ID
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-479">
                          <input type="text" value={tc.defectId} onChange={e => handleUpdateTestCase(tcIndex, 'defectId', e.target.value)} placeholder={isRun ? '[If status is Fail, reference defect tracking ID]' : 'Available after test is run'} disabled={!isRun} className={`w-full text-sm outline-none bg-transparent placeholder:text-stone-400 ${!isRun ? 'cursor-not-allowed' : ''}`} data-id="element-480" />
                        </td>
                      </tr>

                      {/* Notes - editable only after test is run */}
                      <tr className={!isRun ? 'opacity-70' : ''} data-id="element-481">
                        <td className="px-3 py-2.5 font-semibold text-stone-700 border border-stone-200 bg-stone-50/50 align-top" data-id="element-482">
                          Notes
                        </td>
                        <td className="px-3 py-2.5 border border-stone-200" data-id="element-483">
                          <textarea value={tc.notes} onChange={e => handleUpdateTestCase(tcIndex, 'notes', e.target.value)} placeholder={isRun ? '[Any additional observations or comments]' : 'Available after test is run'} rows={2} disabled={!isRun} className={`w-full text-sm outline-none border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white rounded-none px-2 py-1.5 placeholder:text-stone-400 transition-colors duration-100 resize-none ${!isRun ? 'cursor-not-allowed' : ''}`} data-id="element-484" />
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Test Persona Credentials */}
                  {tc.personaIds.length > 0 && <div className="mt-4" data-id="element-485">
                      <table className="w-full border-collapse text-sm" data-id="element-486">
                        <thead data-id="element-487">
                          <tr data-id="element-488">
                            <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff]" colSpan={4} data-id="element-489">
                              Test Persona Credentials
                            </th>
                          </tr>
                          <tr data-id="element-490">
                            <th className="bg-stone-50/50 text-stone-700 text-left px-3 py-2 font-semibold border border-stone-200 w-[25%]" data-id="element-491">
                              Persona
                            </th>
                            <th className="bg-stone-50/50 text-stone-700 text-left px-3 py-2 font-semibold border border-stone-200 w-[25%]" data-id="element-492">
                              Login Email
                            </th>
                            <th className="bg-stone-50/50 text-stone-700 text-left px-3 py-2 font-semibold border border-stone-200 w-[25%]" data-id="element-493">
                              Password
                            </th>
                            <th className="bg-stone-50/50 text-stone-700 text-left px-3 py-2 font-semibold border border-stone-200 w-[25%]" data-id="element-494">
                              Role/Permissions
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-495">
                          {tc.personaIds.map(pid => {
                      const persona = personaOptions.find(p => p.id === pid);
                      if (!persona) return null;
                      const creds = tc.personaCredentials[pid] || {
                        email: '',
                        password: '',
                        rolePermissions: ''
                      };
                      return <tr key={pid} className={isRun ? 'opacity-50' : ''} data-id="element-496">
                                <td className="px-3 py-2.5 text-stone-700 border border-stone-200 bg-stone-50/30 font-medium" data-id="element-497">
                                  {persona.name}
                                </td>
                                <td className="px-3 py-2.5 border border-stone-200" data-id="element-498">
                                  <input type="email" value={creds.email} onChange={e => handleUpdatePersonaCredential(tcIndex, pid, 'email', e.target.value)} placeholder="[Login email]" disabled={isRun} className={`w-full text-sm outline-none bg-transparent placeholder:text-stone-400 ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-499" />
                                </td>
                                <td className="px-3 py-2.5 border border-stone-200" data-id="element-500">
                                  <input type="text" value={creds.password} onChange={e => handleUpdatePersonaCredential(tcIndex, pid, 'password', e.target.value)} placeholder="[Password]" disabled={isRun} className={`w-full text-sm outline-none bg-transparent placeholder:text-stone-400 ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-501" />
                                </td>
                                <td className="px-3 py-2.5 border border-stone-200" data-id="element-502">
                                  <input type="text" value={creds.rolePermissions} onChange={e => handleUpdatePersonaCredential(tcIndex, pid, 'rolePermissions', e.target.value)} placeholder="[Role/Permissions]" disabled={isRun} className={`w-full text-sm outline-none bg-transparent placeholder:text-stone-400 ${isRun ? 'opacity-70 cursor-default' : ''}`} data-id="element-503" />
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>)}

            {/* Add Test Case button */}
            {!isRun && <button type="button" onClick={handleAddTestCase} className="flex items-center gap-1.5 px-3 py-2 text-sm text-stone-500 hover:text-stone-700 border border-dashed border-stone-300 hover:border-stone-400 rounded-none transition-colors duration-100 w-full justify-center" data-id="element-504">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-505" />
                <span data-id="element-506">Add Test Case</span>
              </button>}
          </div>
        </div>
      </div>

      {/* Run Test Button */}
      {!isRun && <div className="flex justify-end" data-id="element-507">
          <button type="button" onClick={onRunTest} disabled={!canRun} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-stone-800 rounded-none hover:bg-stone-700 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-508">
            <PlayIcon className="w-3.5 h-3.5" data-id="element-509" />
            Run Test
          </button>
        </div>}

      {/* Test Results (shown after run) */}
      {isRun && <div className="border-t border-stone-200 pt-6 space-y-8" data-id="element-510">
          {/* SS1: Overall Test Results */}
          <div data-id="element-511">
            <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3" data-id="element-512">
              Overall Test Results
            </label>
            <table className="w-full border-collapse text-sm" data-id="element-513">
              <thead data-id="element-514">
                <tr data-id="element-515">
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-1/3" data-id="element-516">
                    Total Test Cases
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-1/3" data-id="element-517">
                    Passed
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-1/3" data-id="element-518">
                    Failed
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-519">
                <tr data-id="element-520">
                  <td className="px-3 py-2.5 text-stone-700 border border-stone-200 bg-stone-50/30 font-medium" data-id="element-521">
                    {uatTestCases.length}
                  </td>
                  <td className="px-3 py-2.5 text-emerald-700 border border-stone-200 bg-stone-50/30 font-medium" data-id="element-522">
                    {uatTestCases.filter(tc => tc.status === 'Pass').length}
                  </td>
                  <td className="px-3 py-2.5 text-red-600 border border-stone-200 bg-stone-50/30 font-medium" data-id="element-523">
                    {uatTestCases.filter(tc => tc.status === 'Fail').length}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* SS2: Defect Log */}
          <div data-id="element-524">
            <p className="text-sm text-stone-600 mb-3" data-id="element-525">
              Any failures must be documented and tracked using the following
              defect log:
            </p>
            <table className="w-full border-collapse text-sm" data-id="element-526">
              <thead data-id="element-527">
                <tr data-id="element-528">
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[70px]" data-id="element-529">
                    ID
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff]" data-id="element-530">
                    Description
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[120px]" data-id="element-531">
                    Severity
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[120px]" data-id="element-532">
                    Status
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[140px]" data-id="element-533">
                    Agent Fix Ticket
                  </th>

                  <th className="bg-[#0000ff] border border-[#0000ff] w-[36px] font-[600] text-white" data-id="element-534" />
                </tr>
              </thead>
              <tbody data-id="element-535">
                {defects.map((defect, dIdx) => <tr key={dIdx} data-id="element-536">
                    <td className="px-3 py-2.5 text-stone-700 border border-stone-200 bg-stone-50/30 font-medium" data-id="element-537">
                      {defect.id}
                    </td>
                    <td className="px-3 py-2.5 border border-stone-200" data-id="element-538">
                      <input type="text" value={defect.description} onChange={e => handleUpdateDefect(dIdx, 'description', e.target.value)} placeholder="[Describe the defect]" className="w-full text-sm outline-none bg-transparent placeholder:text-stone-400" data-id="element-539" />
                    </td>
                    <td className="px-3 py-2.5 border border-stone-200" data-id="element-540">
                      <select value={defect.severity} onChange={e => handleUpdateDefect(dIdx, 'severity', e.target.value)} className="w-full text-sm outline-none bg-transparent text-stone-700" data-id="element-541">
                        <option value="" data-id="element-542">—</option>
                        <option value="Critical" data-id="element-543">Critical</option>
                        <option value="High" data-id="element-544">High</option>
                        <option value="Medium" data-id="element-545">Medium</option>
                        <option value="Low" data-id="element-546">Low</option>
                      </select>
                    </td>
                    <td className="px-3 py-2.5 border border-stone-200" data-id="element-547">
                      <select value={defect.status} onChange={e => handleUpdateDefect(dIdx, 'status', e.target.value)} className="w-full text-sm outline-none bg-transparent text-stone-700" data-id="element-548">
                        <option value="" data-id="element-549">—</option>
                        <option value="Open" data-id="element-550">Open</option>
                        <option value="In Progress" data-id="element-551">In Progress</option>
                        <option value="Fixed" data-id="element-552">Fixed</option>
                        <option value="Closed" data-id="element-553">Closed</option>
                      </select>
                    </td>
                    <td className="px-3 py-2.5 border border-stone-200" data-id="element-554">
                      <input type="text" value={defect.uatCaseId} onChange={e => handleUpdateDefect(dIdx, 'uatCaseId', e.target.value)} placeholder="[Ticket link or ID]" className="w-full text-sm outline-none bg-transparent placeholder:text-stone-400" data-id="element-555" />
                    </td>
                    <td className="px-3 py-2.5 border border-stone-200" data-id="element-556">
                      <select value={defect.retest} onChange={e => handleUpdateDefect(dIdx, 'retest', e.target.value)} className="w-full text-sm outline-none bg-transparent text-stone-700" data-id="element-557">
                        <option value="" data-id="element-558">—</option>
                        <option value="Yes" data-id="element-559">Yes</option>
                        <option value="No" data-id="element-560">No</option>
                      </select>
                    </td>
                    <td className="px-1 py-2.5 border border-stone-200 text-center" data-id="element-561">
                      <button type="button" onClick={() => handleRemoveDefect(dIdx)} className="w-5 h-5 inline-flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-none transition-colors duration-100" aria-label="Remove defect" data-id="element-562">
                        <Trash2Icon className="w-3 h-3" data-id="element-563" />
                      </button>
                    </td>
                  </tr>)}
                {defects.length === 0 && <tr data-id="element-564">
                    <td colSpan={8} className="px-3 py-4 text-center text-sm text-stone-400 italic border border-stone-200" data-id="element-565">
                      No defects logged yet.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* SS3: Agent Hot Fix */}
          <div data-id="element-566">
            <h3 className="text-lg font-bold text-[#0000ff] mb-3" data-id="element-567">
              Agent Hot Fix
            </h3>
            <table className="w-full border-collapse text-sm" data-id="element-568">
              <thead data-id="element-569">
                <tr data-id="element-570">
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[70px]" data-id="element-571">
                    ID
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff]" data-id="element-572">
                    Description
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[120px]" data-id="element-573">
                    Severity
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[120px]" data-id="element-574">
                    Status
                  </th>
                  <th className="bg-[#0000ff] text-white text-left px-3 py-2 font-semibold border border-[#0000ff] w-[120px]" data-id="element-575">
                    Resolution
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-576">
                <tr data-id="element-577">
                  <td colSpan={5} className="px-3 py-4 text-center text-sm text-stone-400 italic border border-stone-200" data-id="element-578">
                    No agent hot fixes logged yet.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}